import React, { useState, useEffect } from 'react';
import { updateAdminUser } from '../../services/AdminUserApi';
import './EditAdminModal.css';
import Swal from 'sweetalert2';

const EditAdminModal = ({ isOpen, user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    role: 'ADMIN',
    status: 'ACTIVE',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        role: user.role,
        status: user.status,
        notes: user.notes || ''
      });
    }
  }, [user]);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};

    if (formData.notes.length > 500) {
      newErrors.notes = 'Notes must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      await updateAdminUser(user.email, {
        role: formData.role,
        status: formData.status,
        notes: formData.notes
      });

      let successMsg = 'Admin user updated successfully!';
      if (user.role !== formData.role) {
        successMsg = formData.role === 'SUPER_ADMIN'
          ? 'Admin user updated successfully!!'
          : 'Admin user updated successfully!';
      }

      Swal.fire({
        title: "Update Successful",
        text: successMsg,
        icon: "success",
        timerProgressBar: true,
      });
      onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.activatedMessage ||
        error.response?.data?.message ||
        'Failed to update admin user';

      Swal.fire({
        title: "Error",
        text: errorMsg,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const roleChanged = user.role !== formData.role;
  const isPromotion = roleChanged && formData.role === 'SUPER_ADMIN';
  const isDemotion = roleChanged && formData.role === 'ADMIN';

  return (
    <div className="modal-overlay" >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Admin User</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              disabled
              className="input-disabled"
            />
            <small className="help-text">Email cannot be changed</small>
          </div>

          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="ADMIN">ADMIN</option>
              <option value="SUPER_ADMIN">SUPER_ADMIN</option>
            </select>
            {isPromotion && (
              <div className="role-change-notice promotion">
                ⬆️ Promoting to SUPER_ADMIN
              </div>
            )}
            {isDemotion && (
              <div className="role-change-notice demotion">
                ⬇️ Demoting to ADMIN
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Optional notes about this admin user..."
              rows="3"
              maxLength={500}
              className={errors.notes ? 'input-error' : ''}
              disabled={submitting}
            />
            <span className="char-count">
              {formData.notes.length}/500
            </span>
            {errors.notes && <span className="error-text">{errors.notes}</span>}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Updating...' : 'Update Admin User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdminModal;

